import { txt } from '../utils/dom.js';
import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';

// Un testimonio SIN texto deja de comerse la tarjeta.
//
// Caso real (2026-09-15): el modelo metió el testimonio entero —comillas y fecha incluidas—
// dentro de `author` y dejó `text` vacío. El `<p>` del texto lleva `flex-grow:1` dentro de una
// tarjeta `justify-content:space-between`, así que vacío se quedaba TODO el alto libre y la
// tarjeta salía con un boquete entre las comillas y el autor. La causa de raíz (que el modelo
// no sabía qué va en cada campo) se cerró en `llm_schemas.py`, describiendo `text` y `author`;
// esto es el cinturón de render: sin texto no hay hueco, y en el EDITOR se ve un rótulo para
// que el cliente sepa dónde escribir.
//
// El rótulo se emite SIEMPRE y se marca `.editor-only` (el saneo del publicado lo borra): al
// revés —omitirlo cuando no es editor— no funcionaría, porque `compileSiteHtml` publica DESDE
// el editor con `editorMode` en true (gotcha nº12).
const cuerpoTestimonio = (texto, estilo) => {
    const hay = typeof texto === 'string' && texto.trim() !== '';
    const crecer = hay ? 'flex-grow:1;' : '';
    const aviso = hay ? '' : `<span class="editor-only" style="opacity:0.45; font-style:normal; font-size:0.85rem;">Escribe aquí el testimonio</span>`;
    return `<p data-field="text" itemprop="reviewBody" contenteditable="true" style="${estilo} ${crecer}">${txt(texto)}${aviso}</p>`;
};

export const testimonialsBlock = (rawProps) => {
    const p = safe(rawProps);
    const list = Array.isArray(rawProps.list) && rawProps.list.length > 0
        ? rawProps.list
        : [
            { text: "Increíble experiencia, superó todas mis expectativas. El equipo es profesional y atento.", author: "CEO, TechCorp" },
            { text: "Resultados reales desde el primer mes. Sin duda la mejor decisión que hemos tomado.", author: "Directora de Marketing, StartupX" },
            { text: "El servicio al cliente es excepcional. Siempre disponibles y con soluciones rápidas.", author: "Fundador, AgencyPro" },
        ];

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // 3 en desktop, 2 en tablet, 1 en móvil
    const COLS_DESKTOP = 3;
    const useSplide = list.length > COLS_DESKTOP;

    // DUOTONO DE MARCA: la COMILLA decorativa es el ornamento no-CTA por excelencia → estrena
    // el segundo color del cliente. Con `var(--secondary, var(--accent))` un sitio sin
    // secundario se pinta exactamente igual que antes (el CSS cae al acento).
    const DUO = 'var(--secondary, var(--accent))';

    // ── Render de una card (misma estructura para ambos modos) ──────────────
    const renderCard = (t, i, isSplideSlide = false) => {
        let cardStyle, layout;

        if (p.variant === 'v2') {
            cardStyle = `background: transparent; border: none; padding: 20px; display: flex; flex-direction: column; align-items: center; text-align: center; position: relative; height: 100%;`;
            layout  = `<div class="quote-icon" style="color:${DUO}; opacity:0.3; font-size:6rem; line-height:0; margin-bottom:30px; font-family:serif;">"</div>`;
            layout += cuerpoTestimonio(t.text, 'font-size:calc(1.2rem * var(--text-scale, 1)); font-style:italic; line-height:1.7; opacity:0.9; font-weight:300;');
            layout += `<div class="author-line-wrapper" style="margin-top:30px; border-top:1px solid color-mix(in srgb, var(--text) 20%, transparent); padding-top:20px; width:50%;">`;
            layout += `<div data-field="author" itemprop="author" style="font-weight:700; font-size:calc(0.95rem * var(--type-scale, 1)); text-transform:var(--kicker-case, uppercase); letter-spacing:var(--kicker-tracking, 1px);" contenteditable="true">${t.author || 'Cliente Satisfecho'}</div>`;
            layout += `</div>`;
        } else if (p.variant === 'v3') {
            cardStyle = `background: var(--card-bg, color-mix(in srgb, var(--text) 4%, transparent)); border-radius:var(--card-radius, 24px) 24px 24px 0; padding:40px; display:flex; flex-direction:column; position:relative; height:100%;`;
            layout  = cuerpoTestimonio(t.text, 'font-size:calc(1.1rem * var(--text-scale, 1)); line-height:1.8; opacity:0.9;');
            layout += `<div style="margin-top:24px; display:flex; align-items:center; gap:15px;">`;
            layout += `<div style="width:45px; height:45px; border-radius:50%; background:linear-gradient(135deg, var(--accent), var(--text)); opacity:0.8; flex-shrink:0;"></div>`;
            layout += `<div data-field="author" itemprop="author" style="font-weight:700; font-size:calc(0.95rem * var(--text-scale, 1));" contenteditable="true">${t.author || 'Cliente Satisfecho'}</div>`;
            layout += `</div>`;
        } else {
            // v1 default
            cardStyle = `background: var(--card-bg, color-mix(in srgb, var(--text) 8%, transparent)); border:var(--card-border, 1px solid color-mix(in srgb, var(--text) 20%, transparent)); border-radius:var(--card-radius, 24px); padding:40px; display:flex; flex-direction:column; justify-content:space-between; position:relative; height:100%;`;
            layout  = `<div style="color:${DUO}; font-size:3rem; line-height:0.5; margin-bottom:20px; font-family:serif;">"</div>`;
            layout += cuerpoTestimonio(t.text, 'font-size:calc(1.1rem * var(--text-scale, 1)); font-style:italic; line-height:1.8; opacity:0.9;');
            layout += `<div style="margin-top:30px; display:flex; align-items:center; gap:15px;">`;
            layout += `<div style="width:45px; height:45px; border-radius:50%; background:linear-gradient(135deg, var(--accent), var(--text)); opacity:0.7; flex-shrink:0;"></div>`;
            layout += `<div data-field="author" itemprop="author" style="font-weight:700; font-size:calc(0.95rem * var(--text-scale, 1));" contenteditable="true">${t.author || 'Cliente Satisfecho'}</div>`;
            layout += `</div>`;
        }

        const wrapClass = isSplideSlide
            ? `splide__slide testimonial-card sortable-item stagger-item`
            : `testimonial-card sortable-item stagger-item`;

        const wrapStyle = isSplideSlide
            ? `--stagger-i:${i}; ${cardStyle}`
            : `--stagger-i:${i}; flex: 1 1 280px; max-width: 560px; ${cardStyle}`;

        // Microdata Review (schema.org): sobrevive al saneo (no es <script>). Anotación en la
        // página; para rich-result completo necesita itemReviewed (lo aporta un bloque de reseñas
        // del negocio). Mismo patrón reviewBody/author que usará el bloque de reseñas (coordinado).
        return `
        <div class="${wrapClass}" data-item-index="${i}" data-array-name="list" itemscope itemtype="https://schema.org/Review" style="${wrapStyle}">
            ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="list" data-index="${i}">+</button>` : ''}
            <button class="item-add-btn card-add-right" data-array="list" data-index="${i}">+</button>
            <div class="item-tools">
                <button class="tool-btn item-drag-handle" title="Mover" style="background:${btnBg}!important;color:${btnIcon}!important;">${window.icons.drag}</button>
                <button class="tool-btn item-delete-btn" data-array="list" data-index="${i}" title="Borrar" style="background:${btnBg}!important;color:${btnIcon}!important;">${window.icons.trash}</button>
            </div>
            ${layout}
        </div>`;
    };

    // ── Modo FLEX (pocos items, caben en pantalla → centrados) ───────────────
    if (!useSplide) {
        return `
    <section id="bloque-testimonials" class="reveal variant-${p.variant || 'v1'}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding:100px 8%;">
        <h2 data-field="title" contenteditable="true" style="text-align:center; margin-bottom:60px; font-weight:800;">${p.title || 'Lo que dicen de nosotros'}</h2>
        <div class="testimonials-flex-grid">
            ${list.map((t, i) => renderCard(t, i, false)).join('')}
        </div>
    </section>`;
    }

    // ── Modo SPLIDE (muchos items → carousel con scroll) ────────────────────
    return `
    <section id="bloque-testimonials" class="reveal variant-${p.variant || 'v1'}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding:100px 8%;">
        <style>
        /* CSS scoped — no contamina otros Splide */
        .editor-active .testimonials-splide .splide__track { overflow: visible !important; }
        .editor-active .testimonials-splide .splide__list { display: flex !important; flex-wrap: nowrap !important; overflow-x: auto !important; gap: 30px; padding-bottom: 20px; }
        .editor-active .testimonials-splide .splide__slide { flex: 0 0 380px !important; max-width: 85cqw !important; }
        .editor-active .testimonials-splide .splide__list::-webkit-scrollbar { display: none; }
        .editor-active .testimonials-splide .splide__arrows { display: none; }
        </style>

        <h2 data-field="title" contenteditable="true" style="text-align:center; margin-bottom:60px; font-weight:800;">${p.title || 'Lo que dicen de nosotros'}</h2>

        <div class="splide testimonials-splide" data-splide-per-page="3" data-splide-gap="30px">
            <div class="splide__track">
                <div class="splide__list" style="align-items:stretch; margin:0; padding:10px 0;">
                    ${list.map((t, i) => renderCard(t, i, true)).join('')}
                </div>
            </div>
        </div>
    </section>`;
};
